import { useTranslation } from "react-i18next";

import type { NotificationRuntimeInstallState } from "./notificationClient";

export const NOTIFICATION_INSTALL_HINT_CLASSES = {
  root: "notification-install-hint",
  title: "notification-install-hint-title",
  body: "notification-install-hint-body",
  steps: "notification-install-hint-steps",
} as const;

export const NOTIFICATION_PERMISSION_PROMPT_CLASSES = {
  root: "notification-permission-prompt",
  title: "notification-permission-prompt-title",
  body: "notification-permission-prompt-body",
  action: "notification-permission-prompt-action",
} as const;

export interface NotificationInstallHintProps {
  open: boolean;
}

export interface NotificationInstallHintVisibilityInput {
  hasAuthenticatedSession: boolean;
  installState: NotificationRuntimeInstallState;
}

export interface NotificationPermissionPromptProps {
  open: boolean;
  busy?: boolean;
  onEnable: () => void;
}

export interface NotificationPermissionPromptVisibilityInput {
  hasAuthenticatedSession: boolean;
  supported: boolean;
  permission: NotificationPermission | "unsupported";
}

export function shouldShowNotificationInstallHint(input: NotificationInstallHintVisibilityInput): boolean {
  if (!input.hasAuthenticatedSession) {
    return false;
  }
  if (input.installState.runtimeEligible) {
    return false;
  }
  return input.installState.requiresInstall;
}

export function shouldShowNotificationPermissionPrompt(input: NotificationPermissionPromptVisibilityInput): boolean {
  return input.hasAuthenticatedSession && input.supported && input.permission === "default";
}

export function NotificationInstallHint({ open }: NotificationInstallHintProps) {
  const { t } = useTranslation();
  const translatedSteps = t("notifications.install.steps", { returnObjects: true }) as unknown;
  const installSteps = Array.isArray(translatedSteps)
    ? translatedSteps.filter((step): step is string => typeof step === "string" && step.trim() !== "")
    : [];

  if (!open) {
    return null;
  }

  return (
    <aside
      className={NOTIFICATION_INSTALL_HINT_CLASSES.root}
      data-testid="notification-install-hint"
      aria-live="polite"
    >
      <h2 className={NOTIFICATION_INSTALL_HINT_CLASSES.title}>{t("notifications.install.title")}</h2>
      <p className={NOTIFICATION_INSTALL_HINT_CLASSES.body}>{t("notifications.install.body")}</p>
      <ol className={NOTIFICATION_INSTALL_HINT_CLASSES.steps}>
        {installSteps.map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
    </aside>
  );
}

export function NotificationPermissionPrompt({ open, busy = false, onEnable }: NotificationPermissionPromptProps) {
  const { t } = useTranslation();

  if (!open) {
    return null;
  }

  return (
    <aside
      className={NOTIFICATION_PERMISSION_PROMPT_CLASSES.root}
      data-testid="notification-permission-prompt"
      aria-live="polite"
    >
      <h2 className={NOTIFICATION_PERMISSION_PROMPT_CLASSES.title}>{t("notifications.permission.title")}</h2>
      <p className={NOTIFICATION_PERMISSION_PROMPT_CLASSES.body}>{t("notifications.permission.body")}</p>
      <button
        className={`primary-button ${NOTIFICATION_PERMISSION_PROMPT_CLASSES.action}`}
        type="button"
        onClick={onEnable}
        disabled={busy}
      >
        {busy ? t("notifications.permission.enabling") : t("notifications.permission.action")}
      </button>
    </aside>
  );
}
